import type { Routes } from '@angular/router';
import {
  redirectIfAuthenticatedGuard,
  requireAuthenticatedGuard,
} from './core/auth';
import { canActivateWithPermissions, PERM } from './core/rbac';
import { LayoutComponent } from './shared/components/layout/layout.component';

export const routes: Routes = [
  {
    path: 'home',
    canActivate: [redirectIfAuthenticatedGuard],
    loadComponent: () =>
      import('./pages/home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('./pages/forbidden/forbidden.component').then(
        (m) => m.ForbiddenComponent,
      ),
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [requireAuthenticatedGuard],
    children: [
      {
        path: '',
        redirectTo: '/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        canActivate: [canActivateWithPermissions([PERM.dashboardView])],
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then(
            (m) => m.DashboardComponent,
          ),
      },
      {
        path: 'raw-data',
        canActivate: [canActivateWithPermissions([PERM.rawdataView])],
        loadComponent: () =>
          import('./pages/raw-data-list/raw-data-list.component').then(
            (m) => m.RawDataListComponent,
          ),
      },
      {
        path: 'raw-data/new',
        canActivate: [canActivateWithPermissions([PERM.rawdataView])],
        loadComponent: () =>
          import('./pages/raw-data-list/raw-data-form.component').then(
            (m) => m.RawDataFormComponent,
          ),
      },
      {
        path: 'raw-data/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.rawdataView])],
        loadComponent: () =>
          import('./pages/raw-data-list/raw-data-form.component').then(
            (m) => m.RawDataFormComponent,
          ),
      },
      {
        path: 'raw-data/:id',
        canActivate: [canActivateWithPermissions([PERM.rawdataView])],
        loadComponent: () =>
          import('./pages/raw-data-list/raw-data-detail.component').then(
            (m) => m.RawDataDetailComponent,
          ),
      },
      {
        path: 'measure-types',
        canActivate: [canActivateWithPermissions([PERM.measureView])],
        loadComponent: () =>
          import(
            './pages/measure-data-type/measure-data-type-list.component'
          ).then((m) => m.MeasureDataTypeListComponent),
      },
      {
        path: 'measure-types/new',
        canActivate: [canActivateWithPermissions([PERM.measureWrite])],
        loadComponent: () =>
          import(
            './pages/measure-data-type/measure-data-type-form.component'
          ).then((m) => m.MeasureDataTypeFormComponent),
      },
      {
        path: 'measure-types/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.measureWrite])],
        loadComponent: () =>
          import(
            './pages/measure-data-type/measure-data-type-form.component'
          ).then((m) => m.MeasureDataTypeFormComponent),
      },
      {
        path: 'car-campaigns',
        canActivate: [canActivateWithPermissions([PERM.campaignView])],
        loadComponent: () =>
          import('./pages/car-campaign/car-campaign-list.component').then(
            (m) => m.CarCampaignListComponent,
          ),
      },
      {
        path: 'car-campaigns/new',
        canActivate: [canActivateWithPermissions([PERM.campaignWrite])],
        loadComponent: () =>
          import('./pages/car-campaign/car-campaign-form.component').then(
            (m) => m.CarCampaignFormComponent,
          ),
      },
      {
        path: 'car-campaigns/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.campaignWrite])],
        loadComponent: () =>
          import('./pages/car-campaign/car-campaign-form.component').then(
            (m) => m.CarCampaignFormComponent,
          ),
      },
      {
        path: 'car-campaigns/:id/performance',
        canActivate: [canActivateWithPermissions([PERM.performanceView])],
        loadComponent: () =>
          import(
            './pages/car-campaign/car-campaign-performance-dashboard.component'
          ).then((m) => m.CarCampaignPerformanceDashboardComponent),
      },
      {
        path: 'car-campaigns/:id/report',
        canActivate: [canActivateWithPermissions([PERM.reportView])],
        loadComponent: () =>
          import(
            './pages/car-campaign-report/car-campaign-year-report.component'
          ).then((m) => m.CarCampaignYearReportComponent),
      },
      {
        path: 'car-campaigns/:id/feedback',
        canActivate: [canActivateWithPermissions([PERM.campaignWrite])],
        loadComponent: () =>
          import(
            './pages/car-campaign/car-campaign-feedback-page.component'
          ).then((m) => m.CarCampaignFeedbackPageComponent),
      },
      {
        path: 'car-campaigns/:id',
        canActivate: [canActivateWithPermissions([PERM.campaignWrite])],
        loadComponent: () =>
          import('./pages/car-campaign/car-campaign-detail.component').then(
            (m) => m.CarCampaignDetailComponent,
          ),
      },
      {
        path: 'approvers',
        canActivate: [canActivateWithPermissions([PERM.approverRead])],
        loadComponent: () =>
          import('./pages/approver/approver-list.component').then(
            (m) => m.ApproverListComponent,
          ),
      },
      {
        path: 'approvers/new',
        canActivate: [canActivateWithPermissions([PERM.approverWrite])],
        loadComponent: () =>
          import('./pages/approver/approver-form.component').then(
            (m) => m.ApproverFormComponent,
          ),
      },
      {
        path: 'approvers/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.approverWrite])],
        loadComponent: () =>
          import('./pages/approver/approver-form.component').then(
            (m) => m.ApproverFormComponent,
          ),
      },
      {
        path: 'approval',
        loadComponent: () =>
          import('./pages/approval/approval-page.component').then(
            (m) => m.ApprovalPageComponent,
          ),
      },
      {
        path: 'approvals',
        canActivate: [canActivateWithPermissions([PERM.transitionRead])],
        loadComponent: () =>
          import('./pages/approval-inbox/approval-inbox.component').then(
            (m) => m.ApprovalInboxComponent,
          ),
      },
      {
        path: 'customers',
        canActivate: [canActivateWithPermissions([PERM.customerView])],
        loadComponent: () =>
          import('./pages/customer/customer-list.component').then(
            (m) => m.CustomerListComponent,
          ),
      },
      {
        path: 'customers/new',
        canActivate: [canActivateWithPermissions([PERM.customerWrite])],
        loadComponent: () =>
          import('./pages/customer/customer-form.component').then(
            (m) => m.CustomerFormComponent,
          ),
      },
      {
        path: 'customers/:id',
        canActivate: [canActivateWithPermissions([PERM.customerView])],
        loadComponent: () =>
          import('./pages/customer/customer-detail.component').then(
            (m) => m.CustomerDetailComponent,
          ),
      },
      {
        path: 'customers/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.customerWrite])],
        loadComponent: () =>
          import('./pages/customer/customer-form.component').then(
            (m) => m.CustomerFormComponent,
          ),
      },
      {
        path: 'cogenerators',
        canActivate: [canActivateWithPermissions([PERM.chpView])],
        loadComponent: () =>
          import('./pages/cogenerator/cogenerator-list.component').then(
            (m) => m.CogeneratorListComponent,
          ),
      },
      {
        path: 'cogenerators/new',
        canActivate: [canActivateWithPermissions([PERM.chpWrite])],
        loadComponent: () =>
          import('./pages/cogenerator/cogenerator-form.component').then(
            (m) => m.CogeneratorFormComponent,
          ),
      },
      {
        path: 'cogenerators/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.chpWrite])],
        loadComponent: () =>
          import('./pages/cogenerator/cogenerator-form.component').then(
            (m) => m.CogeneratorFormComponent,
          ),
      },
      {
        path: 'cogenerators/:id/details',
        canActivate: [canActivateWithPermissions([PERM.chpView])],
        loadComponent: () =>
          import('./pages/cogenerator/cogenerator-detail.component').then(
            (m) => m.CogeneratorDetailComponent,
          ),
      },
      {
        path: 'reference-readings',
        canActivate: [canActivateWithPermissions([PERM.referencesView])],
        loadComponent: () =>
          import(
            './pages/reference-readings/reference-reading-list.component'
          ).then((m) => m.ReferenceReadingListComponent),
      },
      {
        path: 'data-matrix',
        canActivate: [canActivateWithPermissions([PERM.dataView])],
        loadComponent: () =>
          import('./pages/data-matrix/data-matrix.component').then(
            (m) => m.DataMatrixComponent,
          ),
      },
      {
        path: 'data-validation-matrix',
        canActivate: [canActivateWithPermissions([PERM.datavalidatorWrite])],
        loadComponent: () =>
          import(
            './pages/data-validation-matrix/data-validation-matrix.component'
          ).then((m) => m.DataValidationMatrixComponent),
      },
      {
        path: 'data-calculated-chart',
        canActivate: [canActivateWithPermissions([PERM.dataView])],
        loadComponent: () =>
          import(
            './pages/data-calculated-chart/data-calculated-chart.component'
          ).then((m) => m.DataCalculatedChartComponent),
      },
      {
        path: 'data-raw-chart',
        canActivate: [canActivateWithPermissions([PERM.rawdataView])],
        loadComponent: () =>
          import('./pages/data-raw-chart/data-raw-chart.component').then(
            (m) => m.DataRawChartComponent,
          ),
      },
      {
        path: 'chp-recap',
        canActivate: [canActivateWithPermissions([PERM.chpView])],
        loadComponent: () =>
          import('./pages/chp-recap/chp-recap.component').then(
            (m) => m.ChpRecapComponent,
          ),
      },
      {
        path: 'monthly-metrics-aggregation',
        canActivate: [canActivateWithPermissions([PERM.dataView])],
        loadComponent: () =>
          import(
            './pages/monthly-metrics-aggregation/monthly-metrics-aggregation.component'
          ).then((m) => m.MonthlyMetricsAggregationComponent),
      },
      {
        path: 'cogenerator-config',
        canActivate: [canActivateWithPermissions([PERM.chpConfigView])],
        loadComponent: () =>
          import(
            './pages/cogenerator-config/cogenerator-config-list.component'
          ).then((m) => m.CogeneratorConfigListComponent),
      },
      {
        path: 'cogenerator-config/new',
        canActivate: [canActivateWithPermissions([PERM.chpConfigWrite])],
        loadComponent: () =>
          import(
            './pages/cogenerator-config/cogenerator-config-form.component'
          ).then((m) => m.CogeneratorConfigFormComponent),
      },
      {
        path: 'cogenerator-config/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.chpConfigWrite])],
        loadComponent: () =>
          import(
            './pages/cogenerator-config/cogenerator-config-form.component'
          ).then((m) => m.CogeneratorConfigFormComponent),
      },
      {
        path: 'formulas',
        canActivate: [canActivateWithPermissions([PERM.formulaView])],
        loadComponent: () =>
          import('./pages/formula/formula-list.component').then(
            (m) => m.FormulaListComponent,
          ),
      },
      {
        path: 'formulas/new',
        canActivate: [canActivateWithPermissions([PERM.formulaWrite])],
        loadComponent: () =>
          import('./pages/formula/formula-form.component').then(
            (m) => m.FormulaFormComponent,
          ),
      },
      {
        path: 'formulas/:id/edit',
        canActivate: [canActivateWithPermissions([PERM.formulaWrite])],
        loadComponent: () =>
          import('./pages/formula/formula-form.component').then(
            (m) => m.FormulaFormComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '/home',
  },
];
